<template>
  <DetailCard :loading="loading">
    <template #heading>
      <span class="pi pi-table"></span>
      {{
        comparisonComponentPage === 0
          ? $t('messages.markedtransactions.title')
          : $t('messages.markedtransactions.comparable-table')
      }}
    </template>
    <template #controls>
      <div class="flex gap-2">
        <template v-if="showAiButton">
          <div class="flex items-center gap-2">
            <span>{{ $t('messages.markedtransactions.recency-filter') }}</span>
            <SelectButton
              v-model="evaluationStore.recencyFilter"
              :options="[{ label: '4 kuud', value: 4 }, { label: '12 kuud', value: 12 }]"
              option-label="label"
              option-value="value"
            />
          </div>
          <Button size="sm" @click="openSettingsPopover" severity="secondary" icon="pi pi-cog" v-tooltip.top="`Sarnasuse sätted`" />
          <Popover ref="settingsPopover">
            <div class="flex flex-col gap-4 w-md p-2">
              <div class="text-sm font-semibold text-gray-700 dark:text-gray-300 border-b pb-2">
                Sarnasusotsingu kaalud
              </div>

              <!-- Saved profiles (similarity_config_defaults rows, active first) -->
              <div v-if="profileOptions.length" class="space-y-2">
                <div class="text-xs font-medium text-gray-600 dark:text-gray-400">Salvestatud profiil</div>
                <Select
                  v-model="selectedProfileId"
                  :options="profileOptions"
                  option-label="label"
                  option-value="value"
                  size="small"
                  class="w-full"
                >
                  <template #value="{ value }">
                    <span v-if="value != null" class="text-sm">
                      {{ selectedProfileLabel }}
                      <span v-if="evaluationStore.profileDirty" class="text-orange-500">(muudetud)</span>
                    </span>
                    <span v-else class="text-sm text-gray-400">Vali profiil</span>
                  </template>
                </Select>
              </div>

              <!-- Embedding Weights -->
              <div class="space-y-3">
                <div class="text-xs font-medium text-gray-600 dark:text-gray-400">Mudeli tunnused</div>

                <div class="flex flex-col gap-2">
                  <label class="flex items-center justify-between text-xs">
                    <span>Mudeli tunnus 1</span>
                    <span class="font-mono text-gray-500">{{ evaluationStore.similarityConfig.embedWeights[0].toFixed(2) }}</span>
                  </label>
                  <Slider v-model="evaluationStore.similarityConfig.embedWeights[0]" :min="weightBounds.embed_weights.min" :max="weightBounds.embed_weights.max" :step="weightBounds.embed_weights.step" @change="evaluationStore.markWeightsTouched" />
                </div>

                <div class="flex flex-col gap-2">
                  <label class="flex items-center justify-between text-xs">
                    <span>Mudeli tunnus 2</span>
                    <span class="font-mono text-gray-500">{{ evaluationStore.similarityConfig.embedWeights[1].toFixed(2) }}</span>
                  </label>
                  <Slider v-model="evaluationStore.similarityConfig.embedWeights[1]" :min="weightBounds.embed_weights.min" :max="weightBounds.embed_weights.max" :step="weightBounds.embed_weights.step" @change="evaluationStore.markWeightsTouched" />
                </div>
              </div>

              <!-- Property Features -->
              <div class="space-y-3 pt-2 border-t">
                <div class="text-xs font-medium text-gray-600 dark:text-gray-400">Hooneosa omadused</div>

                <div class="flex flex-col gap-2">
                  <label class="flex items-center justify-between text-xs">
                    <span>Pindala</span>
                    <span class="font-mono text-gray-500">{{ evaluationStore.similarityConfig.embedWeights[2].toFixed(2) }}</span>
                  </label>
                  <Slider v-model="evaluationStore.similarityConfig.embedWeights[2]" :min="weightBounds.embed_weights.min" :max="weightBounds.embed_weights.max" :step="weightBounds.embed_weights.step" @change="evaluationStore.markWeightsTouched" />
                </div>


                <div class="flex flex-col gap-2">
                  <label class="flex items-center justify-between text-xs">
                    <span>Tubade arv</span>
                    <span class="font-mono text-gray-500">{{ evaluationStore.similarityConfig.embedWeights[3].toFixed(2) }}</span>
                  </label>
                  <Slider v-model="evaluationStore.similarityConfig.embedWeights[3]" :min="weightBounds.embed_weights.min" :max="weightBounds.embed_weights.max" :step="weightBounds.embed_weights.step" @change="evaluationStore.markWeightsTouched" />
                </div>
              </div>

              <!-- Distance Weight -->
              <div class="space-y-3 pt-2 border-t">
                <div class="text-xs font-medium text-gray-600 dark:text-gray-400">Asukoht</div>

                <div class="flex flex-col gap-2">
                  <label class="flex items-center justify-between text-xs">
                    <span>Geograafiline kaugus</span>
                    <span class="font-mono text-gray-500">{{ evaluationStore.similarityConfig.zdistWeight.toFixed(2) }}</span>
                  </label>
                  <Slider v-model="evaluationStore.similarityConfig.zdistWeight" :min="weightBounds.zdist_weight.min" :max="weightBounds.zdist_weight.max" :step="weightBounds.zdist_weight.step" @change="evaluationStore.markWeightsTouched" />
                </div>
              </div>

              <!-- Additional Weights -->
              <div class="space-y-3 pt-2 border-t">
                <div class="text-xs font-medium text-gray-600 dark:text-gray-400">Lisakaalud</div>

                <div class="flex flex-col gap-2">
                  <label class="flex items-center justify-between text-xs">
                    <span>Hinna sarnasus</span>
                    <span class="font-mono text-gray-500">{{ evaluationStore.similarityConfig.priceDiffWeight?.toFixed(1) }}</span>
                  </label>
                  <Slider v-model="evaluationStore.similarityConfig.priceDiffWeight" :min="weightBounds.price_diff_weight.min" :max="weightBounds.price_diff_weight.max" :step="weightBounds.price_diff_weight.step" @change="evaluationStore.markWeightsTouched" />
                </div>

                <div class="flex flex-col gap-2">
                  <label class="flex items-center justify-between text-xs">
                    <span>Rikastatud andmete eelistus</span>
                    <span class="font-mono text-gray-500">{{ evaluationStore.similarityConfig.rikastatusWeight?.toFixed(1) }}</span>
                  </label>
                  <Slider v-model="evaluationStore.similarityConfig.rikastatusWeight" :min="weightBounds.rikastatus_weight.min" :max="weightBounds.rikastatus_weight.max" :step="weightBounds.rikastatus_weight.step" @change="evaluationStore.markWeightsTouched" />
                </div>
              </div>

              <!-- Model Selection -->
              <div class="space-y-3 pt-2 border-t">
                <div class="text-xs font-medium text-gray-600 dark:text-gray-400">Sarnasuse mudel</div>
                <Select
                  v-model="similarityPriceModel"
                  :options="catboostModels"
                  option-label="label"
                  option-value="value"
                  size="small"
                  class="w-full"
                />
              </div>

              <!-- Reset Button -->
              <div class="pt-2 border-t">
                <Button
                  size="sm"
                  severity="secondary"
                  outlined
                  @click="evaluationStore.resetWeights"
                  class="w-full"
                >
                  <span class="pi pi-refresh text-xs"></span>
                  Taasta vaikeväärtused
                </Button>
              </div>
            </div>
          </Popover>
          <Button
            size="sm"
            @click="predict"
            :class="[
              'relative overflow-hidden border-transparent',
              aiRunning
                ? 'bg-linear-to-r from-blue-400 via-purple-500 to-cyan-400 animate-ping bg-size-[200%_100%]'
                : 'bg-linear-to-r from-blue-500 to-cyan-500'
            ]"
            :style="aiRunning ? {
              background: 'linear-gradient(-45deg, #3b82f6, #8b5cf6, #06b6d4, #10b981)',
              backgroundSize: '400% 400%',
              animation: 'gradient-shift 2s ease infinite'
            } : {}"
            :loading="aiRunning"
            :disabled="!aiButtonEnabled"
            v-tooltip.top="!aiButtonEnabled ? 'Vali esmalt ehitise osa' : null"
          >
            <span class="text-xs font-semibold px-1 text-white rounded-md">AI</span>

            Leia sarnased tehingud
          </Button>
        </template>

        <Button
          target="marked-transactions-map"
          severity="success"
          size="sm"
          :href="`/transactions/${objectId}${geom ? '/' + geom : ''}`"
          @click="openMap"
        >
          <span class="pi pi-map"></span>
          {{ $t(`messages.detail.comparableTransactions.open-map`) }}
        </Button>
      </div>
    </template>

    <div ref="contentContainer" class="content-container min-h-[120px] relative" :style="containerStyle">
      <!-- RED-262: recompute keeps the table visible; a subtle chip signals the update in place. -->
      <div
        v-if="recomputingAdjustments"
        role="status"
        aria-live="polite"
        class="absolute top-2 right-3 z-10 flex items-center gap-1.5 rounded-full bg-white/85 dark:bg-gray-800/85 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-300 shadow-xs backdrop-blur-sm"
      >
        <i class="pi pi-spin pi-spinner text-[0.7rem]"></i>
        <span>{{ $t('messages.detail.comparableTransactions.updating') }}</span>
      </div>
      <Transition name="fade" mode="out-in" @before-leave="captureHeight" @after-enter="clearHeight">
        <TransactionsSelectionTableComponent v-if="selectionTableDataExists && comparisonComponentPage === 0" key="selection" />
        <div v-else-if="aiRunning || pendingResults" key="loading" role="status" aria-live="polite" class="h-full">
          <div class="flex flex-col items-center justify-center py-10 h-full">
            <div class="flex items-end gap-2 h-8">
              <span class="w-2.5 h-2.5 rounded-full bg-blue-500 animate-bounce motion-reduce:animate-none"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-purple-500 animate-bounce motion-reduce:animate-none [animation-delay:0.15s]"></span>
              <span class="w-2.5 h-2.5 rounded-full bg-cyan-500 animate-bounce motion-reduce:animate-none [animation-delay:0.3s]"></span>
            </div>
            <div class="mt-3 text-sm text-gray-600 dark:text-gray-300">Otsin sarnaseid tehinguid...</div>
          </div>
        </div>
        <ComparisonTable v-else-if="comparisonComponentPage === 1" key="comparison" />
        <h1 v-else key="empty" class="text-4xl border-t border-gray-200 text-gray-500 dark:text-gray-400 dark:border-gray-700 text-center p-3">
          {{ $t(`messages.detail.comparableTransactions.data-missing`) }}
        </h1>
      </Transition>

      <div class="flex gap-2 p-3 bg-gray-50 dark:bg-gray-800">
        <Button v-if="selectionTableDataExists" size="sm" severity="success" data-testid="comparison-page-toggle" @click="next()">
          <span v-if="comparisonComponentPage === 0">
            {{ $t('messages.markedtransactions.create-comparable-table') }}
            <span class="pi pi-chevron-right"></span>
          </span>
          <span v-else>
            <span class="pi pi-chevron-left"></span>
            {{ $t('messages.markedtransactions.back-to-selection-table') }}
          </span>
        </Button>
        <Button size="sm" @click="clear()" v-if="selectionTableDataExists">
          {{ $t('messages.markedtransactions.clear') }}
        </Button>
      </div>
    </div>

    <TransactionsDialog :object-id="Number(objectId)" :object-geom="geom ? Number(geom) : null" />
  </DetailCard>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3'
import { storeToRefs } from 'pinia'
import { useToast } from 'primevue/usetoast'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import TransactionsDialog from '@/components/transactions/TransactionsDialog.vue'
import { useModelResolution } from '@/composables/useModelResolution'
import { useTransactionStore } from '@/stores/transaction'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import { useLandBoardStore } from '@/stores/useLandBoardStore'
import TransactionsSelectionTableComponent from '../../transactions/TransactionsSelectionTableComponent.vue'
import ComparisonTable from '../../transactions/ComparisonTable.vue'
import DetailCard from '../DetailCard.vue'

const landBoardStore = useLandBoardStore()
const transactionStore = useTransactionStore()
const evaluationStore = useEvaluationStore()
const { ensureModelsResolved } = useModelResolution()
const {
  selectionTableRows,
  isTransactionsMapDialogOpen,
  comparisonComponentPage,
} = storeToRefs(transactionStore)

const { t } = useI18n()
const page = usePage()
const toast = useToast()

const props = defineProps({
  objectId: {
    type: [String, Number],
  },
})

const loading = ref(false)
const geom = ref(null)
const settingsPopover = ref(null)
const pendingResults = ref(false)
const contentContainer = ref(null)
const lockedHeight = ref(null)

const isHeightLocked = ref(false)

const containerStyle = computed(() => {
  if (lockedHeight.value) {
    return { minHeight: `${lockedHeight.value}px` }
  }
  return {}
})

const captureHeight = () => {
  if (contentContainer.value) {
    // Disable transition during lock to prevent animation causing shift
    isHeightLocked.value = true
    lockedHeight.value = contentContainer.value.offsetHeight
  }
}

const clearHeight = () => {
  // Re-enable transition before clearing so height animates smoothly to natural size
  isHeightLocked.value = false
  // Small delay to allow transition to re-enable before clearing
  setTimeout(() => {
    lockedHeight.value = null
  }, 50)
}

const aiRunning = computed(() => evaluationStore.aiRunning)
const recomputingAdjustments = computed(
  () => evaluationStore.recomputingAdjustments,
)

const buildingPartSelected = computed(() => {
  return evaluationStore.selectedBuildingPart !== null
})

const landboardHarju = computed(() => {
  return landBoardStore.landBoard.maakond === 'Harju maakond'
})

const showAiButton = computed(() => {
  // RED-267: land valuation is manual-only — no similarity search, no model
  // selection, even inside the Harju coverage area.
  return landboardHarju.value && !evaluationStore.isLandParcel
})

const aiButtonEnabled = computed(() => {
  return landboardHarju.value && buildingPartSelected.value
})

// RED-296: reconfigure the comparison config when the building part resolves
// (apartment vs non-apartment). Lives here rather than in ComparisonTable so
// the null→object flip from the deferred EHR prop is caught even while the
// user sits on the selection table (page 0) and ComparisonTable is unmounted —
// previously the flip was missed and the maatüki rows stuck for the session.
watch(
  () => evaluationStore.selectedBuildingPart,
  (newVal, oldVal) => {
    // RED-267: a land parcel never has a building part; permit-stage EHR
    // buildings on unbuilt parcels must not flip the table out of land mode.
    if (evaluationStore.isLandParcel) {
      return
    }
    const hasBP = newVal !== null
    const hadBP = oldVal !== null
    if (hasBP !== hadBP) {
      transactionStore.reconfigureForPropertyContext(hasBP)
    }
  },
)

const pageProps = computed(() => page.props)

// RED-279: slider ranges from the shared SimilarityWeightBounds (UI scale) so
// calibrated defaults (e.g. zdist 4.48 → 448) are always representable.
// Fallback mirrors App\Services\Valuation\SimilarityWeightBounds::forUi().
const weightBounds = computed(() => pageProps.value?.weightBounds ?? {
  embed_weights: { min: 0, max: 200, step: 1 },
  zdist_weight: { min: 0, max: 600, step: 1 },
  price_diff_weight: { min: 0, max: 3, step: 0.1 },
  rikastatus_weight: { min: 0, max: 5, step: 0.1 },
})

const catboostModels = computed(() =>
  pageProps.value?.modelsAvailable?.models
    ?.filter(m => m.type === 'catboost')
    ?.slice()
    ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    ?.map(model => ({ label: model.name, value: model.model })),
)

const similarityPriceModel = computed({
  get() {
    return evaluationStore.similarityPriceModel
  },
  set(value) {
    evaluationStore.similarityPriceModel = value
    evaluationStore.similarityPriceModelPickedByUser = true
  },
})

// ─── Saved similarity profiles (similarity_config_defaults rows) ─────────────
// Labels are synthesized: the table has no name column, only note/model/run/date.
const shortModelName = type => {
  if (!type) {
    return 'Globaalne'
  }
  return type.replace(/^(EBM|CatBoost)_/, '').replace(/_harjumaa.*$/, '')
}

const profileLabel = profile => {
  const date = new Date(profile.created_at)
  const day = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`
  const run = profile.source_run_id ? ` · #${profile.source_run_id}` : ''
  const active = profile.is_active ? ' · aktiivne' : ''
  return `${shortModelName(profile.model_type)}${run} · ${day}${active}`
}

const profileOptions = computed(() =>
  evaluationStore.similarityProfiles.map(profile => ({
    label: profileLabel(profile),
    value: profile.id,
  })),
)

const selectedProfileLabel = computed(() => {
  const profile = evaluationStore.selectedSimilarityProfile
  return profile ? profileLabel(profile) : ''
})

// Picking a profile loads its weights + anchor and re-predicts immediately,
// matching the kohandustabel's EBM ModelSelector behaviour.
const selectedProfileId = computed({
  get() {
    return evaluationStore.selectedSimilarityProfileId
  },
  set(profileId) {
    const profile = evaluationStore.similarityProfiles.find(p => p.id === profileId)
    if (!profile) {
      return
    }
    evaluationStore.applySimilarityProfile(profile)
    if (aiButtonEnabled.value) {
      predict()
    }
  },
})

onMounted(async () => {
  // Ensure we have full details for all marked transactions
  await transactionStore.loadMarkedTransactionsDetails()

  ensureModelsResolved()
})

const openSettingsPopover = () => {
  settingsPopover.value.toggle(event)
}

const openMap = () => {
  isTransactionsMapDialogOpen.value = true
}

const selectionTableDataExists = computed(() => {
  return !!selectionTableRows.value.length
})

const clear = () => transactionStore.clearComparableTable()

const next = () => {
  const newPage = comparisonComponentPage.value === 1 ? 0 : 1

  comparisonComponentPage.value = newPage

  evaluationStore.save({
    comparison_table_state: {
      comparisonComponentPage: newPage,
    },
  })
}

const predict = async () => {
  // Set pending flag before prediction starts to bridge the gap
  // when aiRunning becomes false but results aren't displayed yet
  pendingResults.value = true

  ensureModelsResolved()

  try {
    const prediction = await evaluationStore.predict()

    if (prediction?.similar_properties?.length > 0) {
      await evaluationStore.updatePrediction(prediction)

      // Update page state first, then clear pending flag
      comparisonComponentPage.value = 1

      evaluationStore.save({
        comparison_table_state: {
          comparisonComponentPage: 1,
        },
      })

      // Clear pending after Vue has processed the state change
      nextTick(() => {
        pendingResults.value = false
      })
    } else {
      pendingResults.value = false
    }
  } catch (err) {
    console.error('AI prediction failed:', err)
    pendingResults.value = false
    toast.add({
      severity: 'warn',
      summary: t('messages.error'),
      detail: t('messages.markedtransactions.ai-unavailable'),
      life: 5000,
    })
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Smooth height transition for the container */
.content-container {
  transition: min-height 0.3s ease;
}
</style>
